// src/pages/Service/ServiceReturn/ServiceReturnEntry.js
// Modelled on Sales Return entry (SalesReturn/Invoice) without stock, serial and
// Tatmeen handling: pick a customer, one of their service vouchers, the
// quantity to return per line, and optionally refund cash/bank now.
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Form, Typography, Flex, Grid, Button, DatePicker, Table, Input, Drawer, Tooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";

import InputField from "../../../components/Input/Input";
import InputNumberField from "../../../components/Input/InputNumber";
import SearchAutoComplete from "../../../components/SearchAutoComplete";
import StaticAutoComplete from "../../../components/StaticAutoComplete";
import NormalPayment from "../../../components/Payment/NormalPayment";
import useMigrationDate from "../../../hooks/useMigrationDate";
import { fetchCustomersList } from "../../SalesReturn/salesReturnThunks";
import { selectCustomersList } from "../../SalesReturn/salesReturnSelectors";
import { getAccountBySeachApi } from "../../../apiservice/setting";
import {
  getServiceReturnVoucherNoApi,
  getReturnableServiceVouchersApi,
  getServiceReturnableDetailsApi,
  createServiceReturnApi,
} from "../../../apiservice/service";
import {
  currentDateTime,
  dateTimeFormat,
  formatToCompanyTimezone,
  formatToUtc,
  getAccessType,
  getDateTimeFromSelectedDate,
  getYearFromCompanyDate,
  numberFormat,
} from "../../../lib/functions";
import { lineReturnAmounts, returnTotals } from "./serviceReturnCalc";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { TextArea } = Input;

// Same date handling as the Service entry payload (utils/serviceEntry.js).
const entryDateToUtc = (entryDate) => {
  const finalEntryDate = dayjs(entryDate).isSame(dayjs(), "day")
    ? entryDate
    : getDateTimeFromSelectedDate(entryDate);
  const dateUTC = formatToUtc(finalEntryDate);
  return { dateUTC, vchYear: getYearFromCompanyDate(formatToCompanyTimezone(dateUTC)) };
};

const ServiceReturnEntry = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [form] = Form.useForm();
  const { disabledDate } = useMigrationDate();

  const [accessType, setAccessType] = useState("");
  const [customer, setCustomer] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [service, setService] = useState(null);
  const [lines, setLines] = useState([]);
  const [returnAccs, setReturnAccs] = useState([]);
  const [returnAcc, setReturnAcc] = useState(null);
  const [entryDate, setEntryDate] = useState(currentDateTime());
  const [voucherNo, setVoucherNo] = useState("");
  const [narration, setNarration] = useState("");
  const [payCart, setPayCart] = useState([]);
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    setAccessType(getAccessType("service_return"));
    getAccountBySeachApi({ multiType: ["service_return"] })
      .then((res) => {
        const accs = res.data || [];
        setReturnAccs(accs);
        if (accs.length) setReturnAcc(accs[0]);
      })
      .catch(() => setReturnAccs([]));
  }, []);

  useEffect(() => {
    if (returnAcc) form.setFieldsValue({ returnAcc: returnAcc.acc_name });
  }, [returnAcc, form]);

  useEffect(() => {
    const { vchYear } = entryDateToUtc(entryDate);
    getServiceReturnVoucherNoApi({ entryDate: vchYear })
      .then((res) => setVoucherNo(res.data || ""))
      .catch(() => setVoucherNo(""));
  }, [entryDate]);

  const resetVoucher = () => {
    setSelectedVoucher(null);
    setService(null);
    setLines([]);
    form.setFieldsValue({ voucher: null });
  };

  const handleCustomerSelect = (cust) => {
    setCustomer(cust || null);
    resetVoucher();
    setVouchers([]);
    form.setFieldsValue({ customer: cust?.acc_name });
    if (!cust?.acc_id) return;
    getReturnableServiceVouchersApi({ customerId: cust.acc_id })
      .then((res) => setVouchers(res.data || []))
      .catch(() => setVouchers([]));
  };

  const handleVoucherSelect = (voucher) => {
    if (!voucher?.service_id) return resetVoucher();
    setSelectedVoucher(voucher);
    form.setFieldsValue({ voucher });
    getServiceReturnableDetailsApi({ service_id: voucher.service_id }).then((res) => {
      setService(res.data?.master || null);
      setLines((res.data?.lines || []).map((l) => ({ ...l, key: l.service_d_id, return_qty: 0 })));
    });
  };

  const setReturnQty = (key, value) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, return_qty: value || 0 } : l)));

  const totals = useMemo(() => returnTotals(lines), [lines]);
  const refundTotal = payCart.reduce((sum, p) => sum + Number(p.tran_amount || 0), 0);

  const handleFooterChange = (value, field) => {
    if (field === "payCart") setPayCart(value || []);
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch {
      return;
    }
    if (!lines.some((l) => Number(l.return_qty) > 0)) {
      Swal.fire("Enter a return quantity for at least one service line.", "", "warning");
      return;
    }
    if (refundTotal > totals.total + 0.005) {
      Swal.fire("Refund cannot be more than the return total.", "", "warning");
      return;
    }

    const { dateUTC, vchYear } = entryDateToUtc(entryDate);
    setSaving(true);
    try {
      const res = await createServiceReturnApi({
        vchdate: vchYear,
        masterData: {
          service_id: selectedVoucher.service_id,
          service_return_acc_id: returnAcc?.acc_id,
          created_date: dateUTC,
          narration,
        },
        itemCart: lines
          .filter((l) => Number(l.return_qty) > 0)
          .map((l) => ({ service_d_id: l.service_d_id, service_r_qty: l.return_qty })),
        payCart: payCart.map((p) => ({ from_acc_id: p.to_acc_id, tran_amount: p.tran_amount })),
      });
      const data = res.data || {};
      if (data.error) {
        Swal.fire(data.message || "Something went wrong", "", "warning");
        return;
      }
      const result = await Swal.fire({
        title: "Service Return Created! Do you want to view voucher?",
        icon: "success",
        showCancelButton: true,
        confirmButtonText: "Yes, view voucher",
        cancelButtonText: "No, back",
      });
      if (result.isConfirmed) {
        navigate(`/service-return?tab=voucher&id=${data.service_r_id}`);
      }
      window.location.reload();
    } catch (err) {
      Swal.fire(err?.response?.data?.message || "Network ERROR", "", "error");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Service",
      width: 300,
      render: (_, record) => (
        <div>
          <div>{record.item_name || ""}</div>
          {record.item_description && (
            <p style={{ margin: 0, fontSize: 12, color: "#666", whiteSpace: "pre-wrap" }}>
              {record.item_description}
            </p>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      render: (_, record) => (
        <InputNumberField value={Number(record.item_qty)} disabled controls={false} style={{ width: "100%" }} />
      ),
    },
    {
      title: "Returnable",
      render: (_, record) => (
        <InputNumberField value={record.returnable_qty} disabled controls={false} style={{ width: "100%" }} />
      ),
    },
    {
      title: "Return Quantity",
      render: (_, record) => (
        <InputNumberField
          min={0}
          max={record.returnable_qty}
          step={1}
          controls={false}
          value={record.return_qty}
          disabled={!record.returnable_qty}
          onChange={(v) => setReturnQty(record.key, v)}
          style={{ width: "100%" }}
          data-testid={`return-qty-${record.key}`}
        />
      ),
    },
    {
      title: "Rate",
      render: (_, record) => (
        <InputNumberField value={Number(record.item_rate)} precision={2} disabled controls={false} style={{ width: "100%" }} />
      ),
    },
    {
      title: "Total",
      render: (_, record) => (
        <InputNumberField
          value={lineReturnAmounts(record, record.return_qty).total}
          precision={2}
          disabled
          controls={false}
          style={{ width: "100%" }}
        />
      ),
    },
  ];

  const summaryRow = (label, value, strong = false) => (
    <Flex justify="space-between" style={{ width: "100%" }}>
      <Text strong={strong}>{label}</Text>
      <Text strong={strong}>{numberFormat(value)}</Text>
    </Flex>
  );

  const actionButtons = (
    <Flex gap={16}>
      <Button size="large" className="traic-button" onClick={() => navigate("/service/dashboard")}>
        Back
      </Button>
      {accessType == "WRITE" && (
        <Button
          type="primary"
          size="large"
          className="traic-button"
          onClick={handleSave}
          loading={saving}
          data-testid="btn-save-service-return"
        >
          Save
        </Button>
      )}
    </Flex>
  );

  return (
    <div className="sales-return-form">
      <Flex justify="space-between" align="center">
        <Title level={3}>Service Return</Title>
        <Tooltip title="Help">
          <Button type="text" icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />} onClick={() => setHelpOpen(true)} />
        </Tooltip>
      </Flex>

      <Form
        form={form}
        layout="horizontal"
        style={{ width: "100%", maxWidth: "1200px" }}
        colon={false}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 18 }}
      >
        {!screens.xs && <div style={{ marginBottom: 24 }}>{actionButtons}</div>}

        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          <Flex vertical gap={10} style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}>
            <Form.Item label="Search Customer" name="customer" rules={[{ required: true, message: "Customer is required" }]}>
              <SearchAutoComplete
                showAddNew={false}
                placeholder="Customer"
                searchThunk={(text) => dispatch(fetchCustomersList(text))}
                resultSelector={selectCustomersList}
                onSelect={(value) => {
                  handleCustomerSelect(value);
                  form.validateFields(["customer"]);
                }}
              />
            </Form.Item>
            <Form.Item label="Institution Name">
              <InputField value={customer?.institution_name} disabled />
            </Form.Item>
            <Form.Item label="Customer Address">
              <InputField value={customer?.address} disabled />
            </Form.Item>
            <Form.Item
              label="Service Return Account"
              name="returnAcc"
              rules={[{ required: true, message: "Service Return Account is required" }]}
            >
              <StaticAutoComplete
                optionsList={returnAccs}
                value={returnAcc}
                placeholder="Select a service return account"
                onSelect={(acc) => {
                  setReturnAcc(acc);
                  form.setFieldsValue({ returnAcc: acc?.acc_name });
                }}
              />
            </Form.Item>
          </Flex>

          <Flex vertical gap={10} style={{ width: !screens.md || !screens.xl ? "100%" : "40%" }}>
            <Form.Item
              label="Service Voucher"
              name="voucher"
              rules={[
                {
                  required: true,
                  validator: (_, value) =>
                    value?.service_id ? Promise.resolve() : Promise.reject(new Error("Service voucher is required")),
                },
              ]}
            >
              <StaticAutoComplete
                optionsList={vouchers.map((v) => ({ id: v.service_id, service_id: v.service_id, display_text: v.display_text }))}
                value={selectedVoucher}
                placeholder={customer ? (vouchers.length ? "Service Voucher" : "No returnable vouchers") : "Select a customer first"}
                onSelect={(v) => {
                  handleVoucherSelect(v);
                  form.validateFields(["voucher"]);
                }}
              />
            </Form.Item>
            <Form.Item label="Entry Date" required>
              <DatePicker
                style={{ width: "100%" }}
                value={entryDate ? dayjs(entryDate) : null}
                onChange={(d) => d && setEntryDate(d.toISOString())}
                disabledDate={(d) =>
                  disabledDate?.(d) ||
                  (service?.created_date && d && d.isBefore(dayjs(formatToCompanyTimezone(service.created_date)), "day"))
                }
                format={dateTimeFormat}
                allowClear={false}
              />
            </Form.Item>
            <Form.Item label="Contact No">
              <InputField value={customer?.contact_no} disabled />
            </Form.Item>
            <Form.Item label="Voucher No">
              <InputField value={voucherNo} disabled />
            </Form.Item>
          </Flex>
        </Flex>
      </Form>

      <div style={{ width: "100%", maxWidth: "1300px" }}>
        <Table
          dataSource={lines}
          columns={columns}
          pagination={false}
          rowKey="key"
          scroll={{ x: "min-content" }}
          locale={{ emptyText: "Select a service voucher to see its lines" }}
          footer={() => (
            <Flex
              gap={50}
              justify="space-between"
              style={{ flexDirection: !screens.md ? "column" : "row", width: "100%" }}
            >
              <Flex vertical gap={10} style={{ width: !screens.md ? "100%" : "50%" }}>
                <Text strong>Refund (optional)</Text>
                <NormalPayment
                  value={payCart}
                  onFooterChange={handleFooterChange}
                  pageType="serviceReturn"
                  accountLabel="Refund From Account"
                />
                <TextArea
                  rows={2}
                  placeholder="Narration"
                  value={narration}
                  onChange={(e) => setNarration(e.target.value)}
                />
              </Flex>
              <Flex vertical gap={6} style={{ width: !screens.md ? "100%" : "35%" }}>
                {summaryRow("Sub Total", totals.subTotal)}
                {summaryRow("Discount", totals.discount)}
                {summaryRow("Tax", totals.tax)}
                {summaryRow("Return Total", totals.total, true)}
                {summaryRow("Refund", refundTotal)}
                {summaryRow("Credited to Customer", totals.total - refundTotal, true)}
              </Flex>
            </Flex>
          )}
        />
      </div>

      <div style={{ marginTop: 32, marginBottom: 32 }}>{actionButtons}</div>

      <Drawer title="Service Return Help" open={helpOpen} onClose={() => setHelpOpen(false)} width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
>
        <div style={{ lineHeight: 1.8 }}>
          <p style={{ color: "#555" }}>
            The Service Return page reverses services that were billed on a service voucher. The
            service income, tax and discount of the returned lines are reversed and the customer
            balance is reduced.
          </p>
          <div style={{ background: "#f5f5f5", padding: "14px", borderRadius: "8px", marginBottom: "16px" }}>
            <strong>Steps</strong>
            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Select the customer</li>
              <li>Choose the service voucher</li>
              <li>Enter the quantity to return for each line</li>
              <li>Optionally add a cash/bank refund</li>
              <li>Click <strong>Save</strong></li>
            </ol>
          </div>
          <div style={{ background: "#fafafa", border: "1px solid #eee", padding: "14px", borderRadius: "8px" }}>
            <strong>Important Notes</strong>
            <ul style={{ paddingLeft: "18px", marginTop: "10px", color: "#666" }}>
              <li>A line cannot be returned more than its billed quantity, across all returns.</li>
              <li>Any amount not refunded stays as a credit on the customer balance.</li>
              <li>A service voucher with returns cannot be edited or deleted until its returns are deleted.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default ServiceReturnEntry;
